/**
 * Item affixes: the rolled properties that make one copy of an item different
 * from another. Each found item rolls a rarity, and its rarity sets how many
 * affixes it gets (`AFFIX_COUNT_BY_RARITY`). Values grow with item level,
 * which comes from how deep in a Crucible the item was found.
 *
 * Numbers are first-pass, to tune.
 */

import type { CharacterEquipmentType, ItemAffixRoll, ItemRarity } from './types/game';
import type { EssenceType } from './types/essence';
import { ESSENCE_TYPE_NAMES, getAllEssenceTypes } from './types/essence';

/** Item levels run from 1 (the start of a faint Crucible) to this. */
export const ITEM_LEVEL_MAX = 10;

/** What an affix changes on the item. */
export type ItemAffixTarget =
    | { readonly kind: 'bonus'; readonly key: 'melee' | 'range' | 'conviction' | 'dodge' | 'speed' | 'resistance' | 'essencePool' }
    | { readonly kind: 'combat'; readonly key: 'accuracy' | 'defense' | 'speedBonus' }
    /** A share more weapon base damage. */
    | { readonly kind: 'damage-percent' }
    /** A share off the essence cost of one essence type's abilities. */
    | { readonly kind: 'ability-cost'; readonly essenceType: EssenceType }
    /** A share off the cooldown of one essence type's abilities. */
    | { readonly kind: 'ability-cooldown'; readonly essenceType: EssenceType }
    /** A share off crystal channel time. */
    | { readonly kind: 'crystal-channel' }
    /** A share of the pool added to Draw's restore. */
    | { readonly kind: 'draw-restore' }
    /** A share more praxis. */
    | { readonly kind: 'praxis-gain' };

/** How an affix value reads on an item card. */
export type ItemAffixFormat = 'flat' | 'percent';

/** The range an affix rolls in, at one item level. */
export interface ItemAffixValueRange {
    readonly min: number;
    readonly max: number;
}

export interface ItemAffixDefinition {
    readonly id: string;
    /** What the value applies to, as it reads after the number ("Melee", "damage"). */
    readonly label: string;
    readonly format: ItemAffixFormat;
    readonly target: ItemAffixTarget;
    /** Which kinds of equipment can roll it. */
    readonly equipmentTypes: readonly CharacterEquipmentType[];
    /** Its range at item level 1, and at `ITEM_LEVEL_MAX`; levels between interpolate. */
    readonly rangeAtLevel1: ItemAffixValueRange;
    readonly rangeAtMaxLevel: ItemAffixValueRange;
    /** Whole numbers only (flat stats); otherwise rolls keep two decimals (three for shares). */
    readonly isInteger: boolean;
    /** How often it rolls, against the other affixes the item can take. */
    readonly weight: number;
    /** Affixes in the same group never roll together on one item. */
    readonly group: string;
    /** The essence it leans to: a Crucible dominant in this essence rolls it more often. */
    readonly essence: EssenceType;
}

const WEAPONS: readonly CharacterEquipmentType[] = ['melee', 'range'];
const JEWELRY: readonly CharacterEquipmentType[] = ['ring', 'amulet'];
const EVERYTHING: readonly CharacterEquipmentType[] = ['melee', 'range', 'shield', 'armor', 'ring', 'amulet'];

const STAT_AFFIXES: readonly ItemAffixDefinition[] = [
    {
        id: 'keen',
        label: 'Melee',
        format: 'flat',
        target: { kind: 'bonus', key: 'melee' },
        equipmentTypes: ['melee', 'ring'],
        rangeAtLevel1: { min: 1, max: 2 },
        rangeAtMaxLevel: { min: 4, max: 6 },
        isInteger: true,
        weight: 10,
        group: 'melee',
        essence: 'fury',
    },
    {
        id: 'marksman',
        label: 'Range',
        format: 'flat',
        target: { kind: 'bonus', key: 'range' },
        equipmentTypes: ['range', 'ring'],
        rangeAtLevel1: { min: 1, max: 2 },
        rangeAtMaxLevel: { min: 4, max: 6 },
        isInteger: true,
        weight: 10,
        group: 'range',
        essence: 'truth',
    },
    {
        id: 'steady',
        label: 'accuracy',
        format: 'flat',
        target: { kind: 'combat', key: 'accuracy' },
        equipmentTypes: WEAPONS,
        rangeAtLevel1: { min: 2, max: 4 },
        rangeAtMaxLevel: { min: 7, max: 10 },
        isInteger: true,
        weight: 10,
        group: 'accuracy',
        essence: 'truth',
    },
    {
        id: 'brutal',
        label: 'weapon damage',
        format: 'percent',
        target: { kind: 'damage-percent' },
        equipmentTypes: WEAPONS,
        rangeAtLevel1: { min: 0.05, max: 0.08 },
        rangeAtMaxLevel: { min: 0.14, max: 0.2 },
        isInteger: false,
        weight: 10,
        group: 'damage',
        essence: 'fury',
    },
    {
        id: 'swift',
        label: 'attack speed',
        format: 'flat',
        target: { kind: 'combat', key: 'speedBonus' },
        equipmentTypes: ['melee', 'range', 'ring'],
        rangeAtLevel1: { min: 1, max: 1 },
        rangeAtMaxLevel: { min: 3, max: 4 },
        isInteger: true,
        weight: 8,
        group: 'attack-speed',
        essence: 'courage',
    },
    {
        id: 'evasive',
        label: 'Dodge',
        format: 'flat',
        target: { kind: 'bonus', key: 'dodge' },
        equipmentTypes: ['armor', 'shield', 'ring', 'amulet'],
        rangeAtLevel1: { min: 1, max: 1 },
        rangeAtMaxLevel: { min: 3, max: 4 },
        isInteger: true,
        weight: 10,
        group: 'dodge',
        essence: 'memory',
    },
    {
        id: 'fleet',
        label: 'Speed',
        format: 'flat',
        target: { kind: 'bonus', key: 'speed' },
        equipmentTypes: ['armor', 'amulet'],
        rangeAtLevel1: { min: 1, max: 1 },
        rangeAtMaxLevel: { min: 2, max: 3 },
        isInteger: true,
        weight: 7,
        group: 'speed',
        essence: 'resonance',
    },
    {
        id: 'warded',
        label: 'Resistance',
        format: 'flat',
        target: { kind: 'bonus', key: 'resistance' },
        equipmentTypes: ['armor', 'shield', 'amulet'],
        rangeAtLevel1: { min: 1, max: 2 },
        rangeAtMaxLevel: { min: 4, max: 6 },
        isInteger: true,
        weight: 10,
        group: 'resistance',
        essence: 'truth',
    },
    {
        id: 'bulwark',
        label: 'defense',
        format: 'flat',
        target: { kind: 'combat', key: 'defense' },
        equipmentTypes: ['armor', 'shield'],
        rangeAtLevel1: { min: 1, max: 1 },
        rangeAtMaxLevel: { min: 3, max: 5 },
        isInteger: true,
        weight: 10,
        group: 'defense',
        essence: 'courage',
    },
    {
        id: 'deep',
        label: 'essence pool',
        format: 'flat',
        target: { kind: 'bonus', key: 'essencePool' },
        equipmentTypes: ['armor', 'ring', 'amulet'],
        rangeAtLevel1: { min: 4, max: 6 },
        rangeAtMaxLevel: { min: 14, max: 20 },
        isInteger: true,
        weight: 10,
        group: 'essence-pool',
        essence: 'sorrow',
    },
    {
        id: 'focused',
        label: 'Conviction',
        format: 'flat',
        target: { kind: 'bonus', key: 'conviction' },
        equipmentTypes: JEWELRY,
        rangeAtLevel1: { min: 1, max: 1 },
        rangeAtMaxLevel: { min: 3, max: 4 },
        isInteger: true,
        weight: 10,
        group: 'conviction',
        essence: 'resonance',
    },
    {
        id: 'flowing',
        label: 'Draw restore',
        format: 'percent',
        target: { kind: 'draw-restore' },
        equipmentTypes: ['armor', 'ring', 'amulet'],
        rangeAtLevel1: { min: 0.01, max: 0.015 },
        rangeAtMaxLevel: { min: 0.03, max: 0.045 },
        isInteger: false,
        weight: 7,
        group: 'draw-restore',
        essence: 'sorrow',
    },
    {
        id: 'quickened',
        label: 'crystal channel time',
        format: 'percent',
        target: { kind: 'crystal-channel' },
        equipmentTypes: JEWELRY,
        rangeAtLevel1: { min: 0.08, max: 0.12 },
        rangeAtMaxLevel: { min: 0.2, max: 0.3 },
        isInteger: false,
        weight: 6,
        group: 'crystal-channel',
        essence: 'memory',
    },
    {
        id: 'practiced',
        label: 'praxis',
        format: 'percent',
        target: { kind: 'praxis-gain' },
        equipmentTypes: EVERYTHING,
        rangeAtLevel1: { min: 0.04, max: 0.06 },
        rangeAtMaxLevel: { min: 0.1, max: 0.15 },
        isInteger: false,
        weight: 5,
        group: 'praxis',
        essence: 'memory',
    },
];

/** One cost and one cooldown affix per essence type; an item takes at most one of each kind. */
const ESSENCE_TYPE_AFFIXES: readonly ItemAffixDefinition[] = getAllEssenceTypes().flatMap((essenceType): ItemAffixDefinition[] => [
    {
        id: `thrift-${essenceType}`,
        label: `${ESSENCE_TYPE_NAMES[essenceType]} ability cost`,
        format: 'percent',
        target: { kind: 'ability-cost', essenceType },
        equipmentTypes: JEWELRY,
        rangeAtLevel1: { min: 0.05, max: 0.08 },
        rangeAtMaxLevel: { min: 0.12, max: 0.18 },
        isInteger: false,
        weight: 3,
        group: 'ability-cost',
        essence: essenceType,
    },
    {
        id: `focus-${essenceType}`,
        label: `${ESSENCE_TYPE_NAMES[essenceType]} ability cooldown`,
        format: 'percent',
        target: { kind: 'ability-cooldown', essenceType },
        equipmentTypes: ['ring', 'amulet', 'melee', 'range'],
        rangeAtLevel1: { min: 0.05, max: 0.08 },
        rangeAtMaxLevel: { min: 0.12, max: 0.18 },
        isInteger: false,
        weight: 3,
        group: 'ability-cooldown',
        essence: essenceType,
    },
]);

/** Every affix an item can roll. */
export const ITEM_AFFIXES: readonly ItemAffixDefinition[] = [...STAT_AFFIXES, ...ESSENCE_TYPE_AFFIXES];

const AFFIXES_BY_ID: ReadonlyMap<string, ItemAffixDefinition> = new Map(ITEM_AFFIXES.map((affix) => [affix.id, affix]));

export function findItemAffix(affixId: string): ItemAffixDefinition | undefined {
    return AFFIXES_BY_ID.get(affixId);
}

/**
 * The essence types an item's affixes carry (its ability cost and cooldown
 * affixes), each once, in affix order: what a dropped item's beam is
 * colored by (#259). Stat affixes carry none.
 */
export function resolveAffixEssenceTypes(affixes: readonly ItemAffixRoll[]): EssenceType[] {
    const essenceTypes: EssenceType[] = [];
    for (const roll of affixes) {
        const target = findItemAffix(roll.affixId)?.target;
        if ((target?.kind === 'ability-cost' || target?.kind === 'ability-cooldown') && !essenceTypes.includes(target.essenceType)) {
            essenceTypes.push(target.essenceType);
        }
    }
    return essenceTypes;
}

/** How many affixes each rarity has: rolled up to Resonant, fixed for named items (`named-items.ts`). */
export const AFFIX_COUNT_BY_RARITY: Readonly<Record<ItemRarity, number>> = {
    common: 0,
    uncommon: 1,
    rare: 2,
    epic: 3,
    legendary: 4,
    mythic: 4,
};

/** Clamps an item level into 1..`ITEM_LEVEL_MAX`. */
export function clampItemLevel(itemLevel: number): number {
    if (!Number.isFinite(itemLevel)) {
        return 1;
    }
    return Math.min(ITEM_LEVEL_MAX, Math.max(1, Math.round(itemLevel)));
}

/** An affix's roll range at one item level, interpolated between level 1 and `ITEM_LEVEL_MAX`. */
export function resolveAffixRange(affix: ItemAffixDefinition, itemLevel: number): ItemAffixValueRange {
    const t = (clampItemLevel(itemLevel) - 1) / (ITEM_LEVEL_MAX - 1);
    const lerp = (from: number, to: number): number => from + (to - from) * t;
    return {
        min: lerp(affix.rangeAtLevel1.min, affix.rangeAtMaxLevel.min),
        max: lerp(affix.rangeAtLevel1.max, affix.rangeAtMaxLevel.max),
    };
}

/** The value an affix takes at `roll` (0..1) through its range at `itemLevel`. */
export function resolveAffixValue(affix: ItemAffixDefinition, itemLevel: number, roll: number): number {
    const range = resolveAffixRange(affix, itemLevel);
    const raw = range.min + (range.max - range.min) * Math.min(1, Math.max(0, roll));
    if (affix.isInteger) {
        return Math.max(1, Math.round(raw));
    }
    const decimals = affix.format === 'percent' ? 3 : 2;
    const factor = 10 ** decimals;
    return Math.round(raw * factor) / factor;
}

/** The affixes a kind of equipment can roll. */
export function listAffixesForEquipmentType(equipmentType: CharacterEquipmentType): ItemAffixDefinition[] {
    return ITEM_AFFIXES.filter((affix) => affix.equipmentTypes.includes(equipmentType));
}

/** How an affix roll reads on an item card, e.g. "+3 Melee", "+12% weapon damage", "-10% Fury ability cost". */
export function describeItemAffix(roll: ItemAffixRoll): string {
    const affix = findItemAffix(roll.affixId);
    if (!affix) {
        return roll.affixId;
    }

    const reduces = affix.target.kind === 'ability-cost'
        || affix.target.kind === 'ability-cooldown'
        || affix.target.kind === 'crystal-channel';
    const sign = reduces ? '-' : '+';
    if (affix.format === 'percent') {
        return `${sign}${Math.round(roll.value * 100)}% ${affix.label}`;
    }
    return `${sign}${roll.value} ${affix.label}`;
}
